import React, { Children, forwardRef, isValidElement, useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { AutoComplete as AntAutoComplete, Cascader as AntCascader, Select as AntSelect, TreeSelect as AntTreeSelect, theme } from 'antd';
import { useLayoutSizing } from './LayoutSizingScope';
import './option-width-control.css';

export function optionText(value) {
  if (typeof value === 'string' || typeof value === 'number') return String(value);
  if (Array.isArray(value)) return value.map(optionText).join(' ');
  if (isValidElement(value)) return optionText(value.props.children);
  return '';
}

export function optionWidthLabels({ options, treeData, children, placeholder, fieldNames = {} }) {
  const labels = [optionText(placeholder)];
  const visit = rows => (rows || []).forEach(row => {
    if (!row) return;
    labels.push(optionText(row[fieldNames.label || 'label'] ?? row.title ?? row.value));
    visit(row[fieldNames.children || 'children'] || row.options);
  });
  visit(options || treeData);
  const visitChildren = nodes => Children.forEach(nodes, child => {
    if (!isValidElement(child)) return;
    labels.push(optionText(child.props.label ?? child.props.children));
    visitChildren(child.props.children);
  });
  visitChildren(children);
  return [...new Set(labels.filter(Boolean))];
}

const controlSelector = '[data-option-width-control], input:not([type="hidden"]), textarea, button, .ant-picker, .ant-input-number, .ant-segmented';
let textContext;
export function longestOptionLabel(labels, font, measure) {
  if (!measure && typeof CanvasRenderingContext2D !== 'undefined') {
    textContext ||= document.createElement('canvas').getContext('2d');
    if (textContext) { textContext.font = font; measure = text => textContext.measureText(text).width; }
  }
  // SSR/jsdom have no text metrics; real browsers use the actual theme font.
  measure ||= text => text.length;
  return labels.reduce((longest, label) => measure(label) > measure(longest) ? label : longest, '');
}

export function sharesControlRow(node) {
  if (!node) return false;
  const scope = node.closest('form, section, .ant-drawer-body, .ant-modal-body, .global-settings-content-body, .suppliers-page, .hrm-page') || node.parentElement?.parentElement || node.parentElement;
  // Read the authored row first: starting a control at 100% must not push its
  // neighbours down and then make that self-created wrap look like a full row.
  for (let child = node, parent = node.parentElement; parent; child = parent, parent = parent.parentElement) {
    const style = window.getComputedStyle(parent);
    // Ant Form.Item contains a horizontal flex wrapper around a *single*
    // content branch. Controls further down that branch may be on other rows.
    const hasOthers = Array.from(parent.children).some(sibling => sibling !== child
      && (sibling.matches(controlSelector) || sibling.querySelector(controlSelector)));
    if (style.display === 'grid') {
      if (window.getComputedStyle(child).gridColumn.includes('1 / -1')) return false;
      if (hasOthers && style.gridTemplateColumns.split(' ').length > 1) return true;
    }
    if (hasOthers && style.display === 'flex' && !style.flexDirection.startsWith('column')) return true;
    if (parent === scope) break;
  }
  const rect = node.getBoundingClientRect();
  if (rect.width && rect.height) {
    return Array.from(scope.querySelectorAll(controlSelector)).some(other => {
      if (node.contains(other) || other.contains(node) || other.closest('.ant-select, .ant-picker, .ant-input-number')?.contains(node)) return false;
      if (other.closest('[data-option-width-control]') && !other.matches('[data-option-width-control]')) return false;
      const next = other.getBoundingClientRect();
      return next.width > 0 && next.height > 0 && Math.min(rect.bottom, next.bottom) - Math.max(rect.top, next.top) > Math.min(rect.height, next.height) / 2;
    });
  }
  return false;
}

function sizedControl(Component, displayName) {
  const Control = forwardRef(function OptionWidthControl({ widthMode, options, treeData, children, placeholder, fieldNames, style, ...props }, ref) {
    const scope = useLayoutSizing(), { token } = theme.useToken();
    const enabled = scope.controls || Boolean(widthMode);
    const longest = useMemo(() => longestOptionLabel(optionWidthLabels({ options, treeData, children, placeholder, fieldNames }), `${token.fontSize}px ${token.fontFamily}`), [options, treeData, children, placeholder, fieldNames, token.fontSize, token.fontFamily]);
    const root = useRef(null), [shared, setShared] = useState(widthMode === 'inline');
    const measure = useCallback(() => {
      const next = widthMode ? widthMode === 'inline' : sharesControlRow(root.current);
      setShared(previous => previous === next ? previous : next);
    }, [widthMode]);
    useLayoutEffect(() => {
      if (!enabled || !root.current) return undefined;
      const node = root.current;
      let frame;
      const schedule = () => { if (frame == null) frame = requestAnimationFrame(() => { frame = null; measure(); }); };
      const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(schedule);
      observer?.observe(node); if (node.parentElement) observer?.observe(node.parentElement);
      const mutations = new MutationObserver(schedule);
      if (node.parentElement) mutations.observe(node.parentElement, { childList: true, subtree: true });
      window.addEventListener('resize', schedule);
      measure();
      return () => { observer?.disconnect(); mutations.disconnect(); window.removeEventListener('resize', schedule); if (frame != null) cancelAnimationFrame(frame); };
    }, [enabled, measure]);
    const dataProps = { options, ...(treeData === undefined ? {} : { treeData }), ...(fieldNames === undefined ? {} : { fieldNames }) };
    if (!enabled) return <Component {...props} {...dataProps} ref={ref} placeholder={placeholder} style={style}>{children}</Component>;
    return <span ref={root} className="option-width-control" data-option-width-control data-width-mode={shared ? 'inline' : 'full'}
      style={{ width: shared ? 'max-content' : '100%', fontFamily: token.fontFamily, fontSize: token.fontSize }}>
      <span aria-hidden="true" className="option-width-label" data-option-label={longest} />
      <Component {...props} {...dataProps} ref={ref} placeholder={placeholder} popupMatchSelectWidth={false}
        style={{ ...style, gridArea: '1 / 1', width: '100%', minWidth: 0, maxWidth: '100%' }}>{children}</Component>
    </span>;
  });
  Control.displayName = displayName;
  // Preserve Option/OptGroup/TreeNode and any library statics used by callers.
  Object.keys(Component).filter(key => !['render', '$$typeof', 'displayName'].includes(key)).forEach(key => { Control[key] = Component[key]; });
  return Control;
}

export const Select = sizedControl(AntSelect, 'OptionWidthSelect');
export const AutoComplete = sizedControl(AntAutoComplete, 'OptionWidthAutoComplete');
export const TreeSelect = sizedControl(AntTreeSelect, 'OptionWidthTreeSelect');
export const Cascader = sizedControl(AntCascader, 'OptionWidthCascader');
